import type { ElementContent, Properties, Root, RootContent } from "hast";

// People and agents both write Library docs, so blocks drop what can run script, load another document, submit a form the editor sits in, or restyle the app.
export const BLOCKED_TAGS: ReadonlySet<string> = new Set([
  "applet",
  "base",
  "button",
  "embed",
  "fencedframe",
  "form",
  "frame",
  "frameset",
  "iframe",
  "input",
  "link",
  "math",
  "meta",
  "noscript",
  "object",
  "portal",
  "script",
  "select",
  "style",
  "template",
  "textarea",
]);

// Handlers; `ref`, which React throws on as a string; and attributes that load a document, submit an app form, or open a dialog or popover above the app.
const BLOCKED_PROPERTY =
  /^(on|ref$|srcDoc$|form$|formAction$|formMethod$|formTarget$|formEncType$|formNoValidate$|popover|interestFor$|commandFor$|command$)/i;

// react-markdown's urlTransform skips `xlink:href` and SVG `href`.
const SCRIPT_URL = /^(javascript|vbscript):/i;

/** For report blocks, after rehype-raw: drops `BLOCKED_TAGS` with their content, SVG animations of links or handlers, `BLOCKED_PROPERTY` attributes and script URLs. */
export function rehypeReportBlockSafety() {
  return (tree: Root) => {
    tree.children = sanitize(tree.children);
  };
}

function sanitize(nodes: readonly RootContent[]): ElementContent[] {
  return nodes.flatMap((node): ElementContent[] => {
    if (node.type === "text") return [node];
    if (node.type !== "element") return [];
    if (BLOCKED_TAGS.has(node.tagName) || animatesLink(node.properties)) {
      return [];
    }
    return [
      {
        ...node,
        properties: keptProperties(node.properties),
        children: sanitize(node.children),
      },
    ];
  });
}

// `<animate attributeName="href" values="javascript:…">` turns an SVG link into a script link; handler attributes get the same treatment.
function animatesLink(properties: Properties): boolean {
  return /href|^on/i.test(String(properties.attributeName ?? ""));
}

function keptProperties(properties: Properties): Properties {
  const kept: Properties = {};
  for (const [name, value] of Object.entries(properties)) {
    if (BLOCKED_PROPERTY.test(name)) continue;
    if (/href$/i.test(name) && isScriptUrl(value)) continue;
    kept[name] = value;
  }
  return kept;
}

function isScriptUrl(value: Properties[string]): boolean {
  // Browsers drop tabs and newlines anywhere in a URL, and spaces and control characters before it.
  const url = String(value).replace(/[\t\n\r]/g, "");
  let start = 0;
  while (start < url.length && url.charCodeAt(start) <= 0x20) start += 1;
  return SCRIPT_URL.test(url.slice(start));
}
